import { Fragment, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { RefreshIcon } from '@heroicons/react/outline'
import PacmanLoader from "react-spinners/PacmanLoader";
import useAxiosPrivate from '../../hooks/useAxiosPrivate';

const providerNames = {
  mobilepay: 'MobilePay',
  stripe: 'Stripe',
};

export default function PaymentResend({ eventSlug, provider }) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [message, setMessage] = useState('')
  const cancelButtonRef = useRef(null)
  const axiosPrivat = useAxiosPrivate();
  const name = providerNames[provider];
  const url = '/payment/' + eventSlug + '/' + provider + '/resend';

  const openDialog = async () => {
    setMessage('')
    setPreview(null)
    setOpen(true)
    setLoading(true)
    try {
      const response = await axiosPrivat.get(url);
      setPreview(response.data)
    } catch (err) {
      console.error(err);
      setMessage('Kunne ikke hente hvem der skal have en ny anmodning.')
    }
    setLoading(false)
  }

  const startResend = async () => {
    setLoading(true)
    try {
      const response = await axiosPrivat.post(url);
      setPreview(null)
      setMessage(response.status === 202
        ? 'Genudsendelsen er startet. Listen opdateres, når den er færdig.'
        : response.data.message)
    } catch (err) {
      console.error(err);
      setMessage(err?.response?.data?.message || 'Genudsendelsen kunne ikke startes.')
    }
    setLoading(false)
  }

  return (
    <>
    <button
      type="button"
      onClick={openDialog}
      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
    >
      Gensend {name}
    </button>

    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={setOpen}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100">
                <RefreshIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  Gensend {name} for dette event
                </Dialog.Title>
                <div className="mt-2 text-sm text-gray-600 space-y-2">
                  { preview && preview.running ?
                    <p>Der kører allerede en genudsendelse for dette event.</p>
                  : preview && preview.sponsors === 0 ?
                    <p>Der er ingen {name}-anmodninger at gensende lige nu.</p>
                  : preview ?
                    <>
                    <p>
                      <strong>{preview.sponsors}</strong> sponsorer får en ny {name}-anmodning
                      på i alt <strong>{preview.amount} kr</strong>.
                    </p>
                    { preview.cancel > 0 ?
                      <p>{preview.cancel} ubetalte anmodninger annulleres først, så ingen kan betale to gange.</p>
                    : '' }
                    <p className="text-gray-500">
                      Det gælder sponsorer, der ikke har betalt, og hvis anmodning er udløbet eller er
                      over {preview.min_age_days} dage gammel uden svar.
                      { provider === 'mobilepay' ? ' Sponsorer der selv har afvist en anmodning i MobilePay, eller hvis anmodning blev afvist af MobilePay ved afsendelse (fx forkert nummer), får ikke en ny.' : '' }
                    </p>
                    </>
                  : '' }
                  { message ? <p className="text-gray-900">{message}</p> : '' }
                </div>
              </div>

              {loading ?
                <PacmanLoader
                  color="#26103c"
                  cssOverride={{margin: "20px auto"}}
                  loading
                  margin={0}
                  size={24}
                  speedMultiplier={1}
                />
              :
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                { preview && !preview.running && preview.sponsors > 0 ?
                  <button
                    type="button"
                    onClick={startResend}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                  >
                    Gensend
                  </button>
                : '' }
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                  onClick={() => setOpen(false)}
                  ref={cancelButtonRef}
                >
                  { preview && !preview.running && preview.sponsors > 0 ? 'Annuller' : 'Luk' }
                </button>
              </div>
              }
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
    </>
  )
}
